// Shared count-then-confirm flow for explorer CSV exports. Owns the dialog state.
import { useCallback, useState } from 'react';

interface ConfirmState {
  open: boolean;
  count: number | null;
  large: boolean;
  loading: boolean;
  error: string | null;
}

const INITIAL: ConfirmState = {
  open: false,
  count: null,
  large: false,
  loading: false,
  error: null,
};

export interface UseExportConfirmArgs {
  fetchCount: () => Promise<{ count: number; large?: boolean }>;
  performExport: () => void | Promise<void>;
}

export interface UseExportConfirmResult {
  handleExport: () => Promise<void>;
  dialogProps: {
    isOpen: boolean;
    count: number | null;
    large: boolean;
    loadingCount: boolean;
    errorMessage: string | null;
    onCancel: () => void;
    onConfirm: () => void;
  };
}

export function useExportConfirm({
  fetchCount,
  performExport,
}: UseExportConfirmArgs): UseExportConfirmResult {
  const [state, setState] = useState<ConfirmState>(INITIAL);

  const close = useCallback(() => setState(INITIAL), []);

  const handleExport = useCallback(async () => {
    setState({ open: true, count: null, large: false, loading: true, error: null });
    try {
      const { count, large } = await fetchCount();
      setState(s => ({ ...s, loading: false, count, large: !!large }));
    } catch (err) {
      // fetchCount throws the server's reason when it gives one.
      const error = err instanceof Error ? err.message : 'Network error while counting rows.';
      setState(s => ({ ...s, loading: false, error }));
    }
  }, [fetchCount]);

  const onConfirm = useCallback(() => {
    close();
    void performExport();
  }, [close, performExport]);

  return {
    handleExport,
    dialogProps: {
      isOpen: state.open,
      count: state.count,
      large: state.large,
      loadingCount: state.loading,
      errorMessage: state.error,
      onCancel: close,
      onConfirm,
    },
  };
}
